// One job, one day — ZT's day panel (1846-1940), reimplemented (round 22).
//
// Opened from a job tile or agenda card, which is several bookings wearing
// one colour — so "edit this" has no single answer and the panel's job is to
// give each booking its own row: tick rows to edit several together, or use
// the per-row Edit and Remove. Rows are derived LIVE from the page's
// bookings state, so a save refreshes the list in place rather than closing
// the panel under the manager's hands.
import { useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { dateRangeLabel, formatClockRange, type DateOnly } from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { isCrewKind } from '../../lib/resourceGroups';
import { nwLabel, type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import { KIND_LABEL } from '../../lib/resourceGroups';
import type { CalendarBooking } from '../../schedulingApi';

export interface JobDayTarget {
  jobId: string;
  date: DateOnly;
}

export function JobDayPanel({
  target,
  bookings,
  worksOn,
  nwInfo,
  onClose,
  onEdit,
  onEditMany,
  onRemove,
  onAdd,
}: {
  target: JobDayTarget;
  /** The page's live bookings — the panel filters, never copies. */
  bookings: CalendarBooking[];
  worksOn: WorksOn;
  nwInfo: NwLookup;
  onClose: () => void;
  onEdit: (booking: CalendarBooking) => void;
  /** Tick several rows, change their dates or times together (batch 8). */
  onEditMany: (bookings: CalendarBooking[]) => void;
  onRemove: (booking: CalendarBooking) => void;
  /** Opens the create modal pre-filled with this job and day. */
  onAdd: (jobId: string, date: DateOnly) => void;
}) {
  const [ticked, setTicked] = useState<Set<string>>(new Set());

  const rows = useMemo(
    () =>
      bookings
        .filter((b) => b.jobId === target.jobId && worksOn(b, target.date))
        // People first, then machines, alphabetical inside each — the order a
        // manager reads a crew list in.
        .sort((a, b) => {
          const aCrew = isCrewKind(a.resourceKind) ? 0 : 1;
          const bCrew = isCrewKind(b.resourceKind) ? 0 : 1;
          return aCrew - bCrew || a.resourceName.localeCompare(b.resourceName);
        }),
    [bookings, target, worksOn]
  );

  const first = rows[0];
  const selected = rows.filter((r) => ticked.has(r.id));
  const nw = nwInfo(target.date);

  const longDate = new Date(`${target.date}T00:00:00`).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const toggle = (id: string) =>
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const colour = first ? jobColour(first.jobNumber, first.jobId, false) : null;

  return (
    <Modal
      title={longDate}
      onClose={onClose}
      size="xl"
      dismissOnBackdrop
      footer={
        <>
          <button
            type="button"
            className="btn-secondary btn-sm mr-auto"
            onClick={() => onAdd(target.jobId, target.date)}
          >
            Add resources
          </button>
          {/* Close first, primary last — same order as every other dialog. */}
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Close
          </button>
          {selected.length > 0 && (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => onEditMany(selected)}
            >
              Edit {selected.length} selected
            </button>
          )}
        </>
      }
    >
      {first && (
        <div className="mb-3">
          <h3
            className={`inline-block rounded px-1.5 py-0.5 text-base font-bold ${colour!.bar} ${colour!.barText}`}
          >
            {first.jobTitle}
          </h3>
          <div className="mt-0.5 font-mono text-xs text-gray-500">{first.jobNumber}</div>
          {nw && (
            <span className="mt-1.5 inline-block rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-red-700">
              {nw.dated ? nwLabel(nw) : 'Weekend'}
            </span>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-md border border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
          Nothing booked on this job for this day any more.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {rows.map((booking) => {
            const isTicked = ticked.has(booking.id);
            const rego = booking.resourceRegistration?.trim();
            const identifier = booking.resourceIdentifier?.trim();
            const label = identifier
              ? `${booking.resourceName} (${identifier})`
              : booking.resourceName;

            return (
              <li
                key={booking.id}
                className={`flex items-center gap-3 px-3 py-2.5 ${isTicked ? 'bg-brand-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isTicked}
                  onChange={() => toggle(booking.id)}
                  aria-label={`Select ${booking.resourceName}`}
                  className="h-5 w-5 flex-none rounded border-gray-300 text-brand-600 focus:ring-brand-500 sm:h-4 sm:w-4"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-800">
                    {label}
                    <span className="ml-1.5 text-xs font-normal text-gray-500">
                      {KIND_LABEL[booking.resourceKind]}
                    </span>
                    {booking.inConflict && (
                      <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[0.65rem] font-medium text-red-700 ring-1 ring-inset ring-red-600/25">
                        Needs re-planning
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {/* Rego on the DETAIL line, not the title — the title
                        truncates, and a clipped plate reads as a missing one
                        (ZT's comment, kept). */}
                    {rego && <span className="font-mono">Rego {rego} · </span>}
                    {dateRangeLabel(booking.startDate, booking.endDate)}
                    {' · '}
                    {formatClockRange(booking.startTime, booking.endTime) ?? 'All day'}
                    {booking.note && <span className="text-gray-400"> · {booking.note}</span>}
                  </div>
                </div>
                <div className="flex flex-none flex-col items-end gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                  <button type="button" className="row-action" onClick={() => onEdit(booking)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="row-action-danger"
                    onClick={() => onRemove(booking)}
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-2 text-xs text-gray-500">
        Each resource keeps its own dates. Tick several to change their dates or times together.
      </p>
    </Modal>
  );
}
